import React, { useState } from "react";
import { Button, Checkbox, Popconfirm, Spin, Tag, Empty } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faSync, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import VideoProductPreview from "./video-product-preview";
import StockStatusTag from "./stock-status-tag";
import VideoStatusTag from "./video-status-tag";

// Ưu tiên trạng thái "xấu nhất" để cảnh báo ở cấp video (giống video-table).
function worstStatus(products = []) {
    if (!products.length) return null;
    if (products.some((p) => p.status === "error")) return "error";
    if (products.some((p) => p.status === "out_of_stock")) return "out_of_stock";
    if (products.some((p) => p.status === "unknown")) return "unknown";
    return "available";
}

// 1 card video trên mobile: checkbox + thumbnail + info + nút thao tác + mở rộng xem sản phẩm.
function VideoCard({ video, checked, onCheck, onEdit, onDelete, onRecheck }) {
    const [expanded, setExpanded] = useState(false);
    const products = video.products || [];
    const ws = worstStatus(products);

    return (
        <div className="border rounded-lg bg-surface p-3 shadow-sm">
            <div className="flex gap-3">
                <Checkbox checked={checked} onChange={(e) => onCheck(video.id, e.target.checked)} className="self-start pt-1" />
                <img
                    src={video.thumbnail || ""}
                    alt=""
                    className="w-14 h-20 object-cover rounded bg-surface-raised flex-shrink-0"
                    onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                />
                <div className="flex-1 min-w-0">
                    <div className="font-medium leading-snug line-clamp-2 break-words">
                        {video.productName || video.caption || "-"}
                    </div>
                    {video.author && <div className="text-xs text-content-3 truncate">@{video.author}</div>}
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                        <VideoStatusTag status={video.videoStatus} />
                        {ws && ws !== "available" && <StockStatusTag status={ws} />}
                        <Tag className="m-0">#{video.id}</Tag>
                        {video.categoryId && <Tag className="m-0">{video.categoryId}</Tag>}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between mt-2 pt-2 border-t">
                {products.length ? (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="flex items-center gap-2 text-sm text-content-2"
                    >
                        <span className="flex -space-x-2">
                            {products.slice(0, 4).map((p) => (
                                <img
                                    key={p.id}
                                    src={p.imageUrl || ""}
                                    alt=""
                                    className="w-7 h-7 rounded border border-white object-cover bg-surface-raised"
                                    onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                                />
                            ))}
                        </span>
                        {products.length} SP
                        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="text-xs text-content-3" />
                    </button>
                ) : (
                    <span className="text-sm text-content-3">Chưa gắn sản phẩm</span>
                )}
                <div className="flex gap-2">
                    <a href={video.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm self-center">
                        Xem
                    </a>
                    <Button size="small" type="primary" icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => onEdit(video)} />
                    <Button size="small" icon={<FontAwesomeIcon icon={faSync} />} onClick={() => onRecheck(video.id)} />
                    <Popconfirm title="Xoá video này?" onConfirm={() => onDelete(video.id)} okText="Xoá" cancelText="Huỷ" okButtonProps={{ danger: true }}>
                        <Button size="small" danger icon={<FontAwesomeIcon icon={faTrash} />} />
                    </Popconfirm>
                </div>
            </div>

            {expanded && products.length > 0 && (
                <div className="flex flex-col gap-2 mt-2">
                    {products.map((p) => (
                        <VideoProductPreview key={p.id} product={p} />
                    ))}
                </div>
            )}
        </div>
    );
}

// Danh sách card cho mobile — thay thế VideoTable, dùng chung state selectedRowKeys.
function VideoCardList({ videos, loading, selectedRowKeys, onSelectChange, onEdit, onDelete, onRecheck }) {
    const allChecked = videos.length > 0 && selectedRowKeys.length === videos.length;
    const someChecked = selectedRowKeys.length > 0 && !allChecked;

    const handleCheck = (id, checked) => {
        onSelectChange(checked ? [...selectedRowKeys, id] : selectedRowKeys.filter((k) => k !== id));
    };

    return (
        <Spin spinning={loading}>
            <div className="flex flex-col gap-2">
                {videos.length > 0 && (
                    <label className="flex items-center gap-2 px-1 text-sm text-content-2">
                        <Checkbox
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={(e) => onSelectChange(e.target.checked ? videos.map((v) => v.id) : [])}
                        />
                        Chọn tất cả trang này
                    </label>
                )}
                {videos.map((v) => (
                    <VideoCard
                        key={v.id}
                        video={v}
                        checked={selectedRowKeys.includes(v.id)}
                        onCheck={handleCheck}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onRecheck={onRecheck}
                    />
                ))}
                {!videos.length && !loading && <Empty description="Không có video" className="py-8" />}
            </div>
        </Spin>
    );
}

export default VideoCardList;
